import { useEffect, useState } from "react";
import { Accessibility, X, Minus, Plus, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const KEY = "eh_a11y";
const DEFAULTS = { scale: 100, contrast: false, dyslexia: false, underline: false, spacing: false, pause: false, focus: false };
const TOGGLES = [
  ["contrast", "High contrast"], ["dyslexia", "Dyslexia-friendly font"], ["underline", "Underline links"],
  ["spacing", "Text spacing"], ["pause", "Pause animations"], ["focus", "Highlight focus"],
];

const load = () => { try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY)) }; } catch { return DEFAULTS; } };

export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(load);

  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = `${s.scale}%`;
    TOGGLES.forEach(([k]) => root.classList.toggle(`a11y-${k}`, !!s[k]));
    localStorage.setItem(KEY, JSON.stringify(s));
  }, [s]);

  const scale = (d) => setS((p) => ({ ...p, scale: Math.min(150, Math.max(80, p.scale + d)) }));

  return (
    <div className="fixed bottom-5 right-5 z-50 no-print">
      {open && (
        <div role="dialog" aria-label="Accessibility options" data-testid="accessibility-panel"
          className="mb-3 w-72 rounded-2xl border border-[#E2E8F0] bg-white shadow-2xl p-5 fade-up">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[#0B2150]">Accessibility</h2>
            <button onClick={() => setOpen(false)} aria-label="Close accessibility options" className="p-1 rounded hover:bg-slate-100" data-testid="accessibility-close-btn"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#334155]">Text size</span>
            <div className="flex items-center gap-2">
              <button onClick={() => scale(-10)} aria-label="Decrease text size" data-testid="a11y-font-decrease" className="h-8 w-8 rounded-full border grid place-items-center hover:bg-slate-100"><Minus className="h-3.5 w-3.5" /></button>
              <span className="text-sm w-10 text-center tabular-nums" data-testid="a11y-font-value">{s.scale}%</span>
              <button onClick={() => scale(10)} aria-label="Increase text size" data-testid="a11y-font-increase" className="h-8 w-8 rounded-full border grid place-items-center hover:bg-slate-100"><Plus className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          {TOGGLES.map(([k, label]) => (
            <label key={k} className="flex items-center justify-between py-2 text-sm text-[#334155] cursor-pointer">
              {label}
              <Switch checked={s[k]} onCheckedChange={(v) => setS((p) => ({ ...p, [k]: v }))} data-testid={`a11y-toggle-${k}`} aria-label={label} />
            </label>
          ))}
          <button onClick={() => setS(DEFAULTS)} data-testid="a11y-reset-btn" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border px-3 py-2 text-sm hover:bg-slate-50">
            <RotateCcw className="h-3.5 w-3.5" /> Reset all
          </button>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Accessibility options" data-testid="accessibility-widget-toggle"
        className="ml-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#0B2150] text-white shadow-xl hover:bg-[#03A896] transition-colors">
        <Accessibility className="h-7 w-7" />
      </button>
    </div>
  );
};
